import { useState, useEffect } from "react";
import { Search, Filter, Calendar, User, FileText, DollarSign, Table, X, CreditCard, Landmark, Banknote, Gift } from "lucide-react";
import api from "../../../api";
import "jspdf-autotable";
import Swal from "sweetalert2";
import Menu from "../../../components/Menu";
import moment from "moment";
import DownloadButtonCaixa from "../../components/DownloadButtonCaixa";
import pix from '../../../assets/img/pix-selected.png';
import LoadingBlock from "../../../components/LoadingBlock";

const Cashiers = () => {
  const [filters, setFilters] = useState({
    date_from: moment().format("YYYY-MM-DD"),
    date_to: moment().format("YYYY-MM-DD"),
    executing_company: "",
    requesting_company: "",
    procedure_name: "",
    patient_name: "",
    payment_method: "",
  });

  const [paymentTotals, setPaymentTotals] = useState({
    credit: 0,
    debit: 0,
    cash: 0,
    courtesy: 0,
  });

  const [data, setData] = useState([]);

  const [totals, setTotals] = useState({
    total_gross: 0,
    total_cost: 0,
    total_net: 0,
  });

  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // Função para buscar dados da API
  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await api.post("/api/cashiers/report", {
        ...filters,
        procedure_name: filters.procedure_name || null,
        payment_method: filters.payment_method || null,
        date_from: filters.date_from || moment().subtract(1, "days").format("YYYY-MM-DD"),
        date_to: filters.date_to || moment().format("YYYY-MM-DD"),
      });
      setData(response.data.data ?? []);
      setTotals(
        response.data.totals ?? {
          total_gross: 0,
          total_cost: 0,
          total_net: 0,
        }
      );
      setPaymentTotals(aggregatePayments(response.data.data ?? []));
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Erro ao buscar dados",
        text: "Ocorreu um erro ao buscar dados. Por favor, tente novamente.",
        showConfirmButton: false,
        timer: 1500,
      });
    } finally {
      setLoading(false);
    }
  };

  const aggregatePayments = (transactions = []) => {
    const sums = { pix: 0, credit: 0, debit: 0, cash: 0, courtesy: 0 };

    transactions.forEach((tx) => {
      tx.payments.forEach((p) => {
        if (sums[p.method] !== undefined) {
          sums[p.method] += parseFloat(p.amount || 0);
        }
      });
    });

    console.log(sums);

    return sums;
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Função para limpar filtros
  const clearFilters = () => {
    setFilters({
      date_from: "",
      date_to: "",
      executing_company: "",
      requesting_company: "",
      procedure_name: "",
      patient_name: "",
      payment_method: "",
    });
  };

  // Formata valor monetário em BRL
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(parseFloat(value) || 0);
  };

  // Formata data para DD/MM/AAAA
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("pt-BR");
  };

  // Exporta um CSV onde cada linha corresponde a um procedimento
  const exportData = () => {
    const header = [
      "Data",
      "Paciente",
      "Procedimento",
      "Executante",
      "Solicitante",
      "Valor Total",
      "Status",
    ];

    // para cada transação (item), iteramos em item.items
    const rows = data.flatMap((item) =>
      item.items.map((sub) => [
        formatDate(item.created_at),
        item.patient.name,
        sub.procedure_name,
        sub.executing_company || "",
        sub.requesting_company || "",
        item.total,
        item.status === "paid" ? "Pago" : "Pendente",
      ])
    );

    const csvContent = [header, ...rows].map((row) => row.join(",")).join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "relatorio-caixa.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };
  const screenTela = (
    <>
      {loading && <LoadingBlock />}

      <div
        className="container-fluid py-4"
        style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
        <div
          className="container-xl"
          style={{ height: "84vh", overflowY: "auto", marginBottom: "15px" }}>
          {/* Header */}
          <div className="card shadow-sm mb-4">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <div className="bg-primary bg-opacity-10 p-2 rounded me-3">
                    <DollarSign size={24} className="text-primary" />
                  </div>
                  <div>
                    <h1 className="h3 mb-1 fw-bold">Relatório de Caixa</h1>
                    <p className="text-muted mb-0">
                      Gerencie pacientes, procedimentos e transações
                    </p>
                  </div>
                </div>
                <div className="d-flex gap-3 align-items-center">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="btn btn-outline-secondary d-flex align-items-center gap-2">
                    <Filter size={16} />
                    {
                      showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'
                    }
                  </button>
                  <button
                    onClick={exportData}
                    className="btn btn-success d-flex align-items-center gap-2">
                    <Table size={16} />
                    Exportar
                  </button>
                  <DownloadButtonCaixa
                    filtros={filters}
                    data={data}
                    totals={totals}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Filtros */}
          {showFilters && (
            <div className="card shadow-sm mb-4">
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label fw-medium">
                      <Calendar size={16} className="me-1" />
                      Data Inicial
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={filters.date_from}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          date_from: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label fw-medium">
                      <Calendar size={16} className="me-1" />
                      Data Final
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={filters.date_to}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          date_to: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label fw-medium">
                      <DollarSign size={16} className="me-1" />
                      Método de Pagamento
                    </label>
                    <select
                      className="form-select"
                      value={filters.payment_method}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          payment_method: e.target.value,
                        }))
                      }>
                      <option value="">Todos</option>
                      <option value="pix">PIX</option>
                      <option value="credit">Cartão de crédito</option>
                      <option value="debit">Cartão de débito</option>
                      <option value="cash">Dinheiro</option>
                      <option value="courtesy">Cortesia</option>
                    </select>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-medium">
                      <User size={16} className="me-1" />
                      Nome do Paciente
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Digite o nome do paciente"
                      value={filters.patient_name?.toUpperCase()}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          patient_name: e.target.value.toUpperCase(),
                        }))
                      }
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-medium">
                      <FileText size={16} className="me-1" />
                      Procedimento
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Digite o nome do procedimento"
                      value={filters.procedure_name?.toUpperCase()}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          procedure_name: e.target.value.toUpperCase(),
                        }))
                      }
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-medium">
                      <User size={16} className="me-1" />
                      Executante
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Digite o nome do executante"
                      value={filters.executing_company?.toUpperCase()}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          executing_company: e.target.value.toUpperCase(),
                        }))
                      }
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-medium">
                      <User size={16} className="me-1" />
                      Solicitante
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Digite o nome do solicitante"
                      value={filters.requesting_company?.toUpperCase()}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          requesting_company: e.target.value.toUpperCase(),
                        }))
                      }
                    />
                  </div>
                </div>

                <div className="d-flex justify-content-end gap-2 mt-4">
                  <button
                    onClick={clearFilters}
                    className="btn btn-outline-secondary d-flex align-items-center gap-2">
                    <X size={16} />
                    Limpar
                  </button>
                  <button
                    onClick={fetchData}
                    disabled={loading}
                    className="btn btn-primary d-flex align-items-center gap-2">
                    <Search size={16} />
                    {loading ? "Buscando..." : "Buscar"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Totalizadores */}
          <div className="row g-4 mb-4">
            <div className="col-md-4">
              <div className="card shadow-sm h-100">
                <div className="card-body">
                  <div
                    className="d-flex justify-content-between align-items-center"
                    title="Soma de todos os valores cobrados dos pacientes (valor total da venda dos procedimentos), antes de aplicar qualquer taxa, custo ou desconto."
                  >
                    <div>
                      <p
                        className="text-muted mb-1 fw-medium">
                        Total Venda
                      </p>
                      <h3 className="mb-0 fw-bold">
                        {formatCurrency(totals.total_gross)}
                      </h3>
                    </div>
                    <div className="bg-primary bg-opacity-10 p-3 rounded">
                      <DollarSign size={24} className="text-primary" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="card shadow-sm h-100">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <p className="text-muted mb-1 fw-medium">Total Repasse</p>
                      <h3 className="mb-0 fw-bold">
                        {formatCurrency(totals.total_cost)}
                      </h3>
                    </div>
                    <div className="bg-warning bg-opacity-10 p-3 rounded">
                      <DollarSign size={24} className="text-warning" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="card shadow-sm h-100">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <p
                        className="text-muted mb-1 fw-medium">
                        Total Achemed
                      </p>
                      <h3 className="mb-0 fw-bold">
                        {/* {formatCurrency(totals.total_net)} */}
                        {formatCurrency(totals.total_gross - totals.total_cost)}
                      </h3>
                    </div>
                    <div className="bg-success bg-opacity-10 p-3 rounded">
                      <DollarSign size={24} className="text-success" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tabela de Resultados: UMA LINHA POR PROCEDIMENTO */}
          <div
            className="card shadow-sm"
            style={{
              maxHeight: "500px", // menor para forçar scroll
              overflowY: "auto",
            }}>
            <div className="card shadow-sm">
              <div className="card-header bg-white">
                <h5 className="my-2 fw-semibold">
                  Resultados ({data.length}{" "}
                  {data.length === 1 ? "pagamento" : "pagamentos"}, {data.reduce((acc, item) => acc + item.items.length, 0)}{" "}
                  {data.reduce((acc, item) => acc + item.items.length, 0) === 1 ? "procedimento" : "procedimentos"})
                </h5>
              </div>

              <div className="card-body p-0">
                <div
                  className="table-responsive"
                  style={{
                    maxHeight: "350px",
                    overflowY: "auto",
                  }}>
                  <table className="table table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th scope="col">Data</th>
                        <th scope="col">Paciente</th>
                        <th scope="col">Procedimento</th>
                        <th scope="col">Executante</th>
                        <th scope="col">Solicitante</th>
                        <th scope="col">Total</th>
                        <th scope="col">Status</th>
                        <th scope="col">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.map((item) =>
                        item.items.map((sub) => (
                          <tr key={sub.id}>
                            <td>{formatDate(item.created_at)}</td>
                            <td>
                              <div className="fw-medium">
                                {item.patient.name}
                              </div>
                              <small className="text-muted">
                                CPF: {item.patient.cpf}
                              </small>
                            </td>
                            <td>{sub.procedure_name}</td>
                            <td>{sub.executing_company || "N/A"}</td>
                            <td>{sub.requesting_company || "N/A"}</td>
                            <td className="fw-bold">
                              {formatCurrency(sub.selling_price)}
                            </td>
                            <td>
                              <span
                                className={`badge ${item.status === "paid"
                                  ? "bg-success"
                                  : "bg-warning"
                                  }`}>
                                {item.status === "paid" ? "Pago" : "Pendente"}
                              </span>
                            </td>
                            <td>
                              <button
                                onClick={() => setSelectedItem(item)}
                                className="btn btn-sm btn-outline-primary">
                                Ver Detalhes
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {selectedItem && (
            <div
              className="modal show d-block"
              style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
              <div className="modal-dialog modal-lg">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Detalhes da Transação</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setSelectedItem(null)}></button>
                  </div>
                  <div className="modal-body">
                    {/* ---------- INÍCIO DA LINHA PRINCIPAL ---------- */}
                    <div className="row g-3">
                      <div className="col-md-12">
                        <strong>Paciente:</strong>
                        <p className="mb-2">{selectedItem.patient.name}</p>
                      </div>
                      <div className="col-md-6">
                        <strong>CPF:</strong>
                        <p className="mb-2">{selectedItem.patient.cpf}</p>
                      </div>
                      <div className="col-md-6">
                        <strong>Subtotal:</strong>
                        <p className="mb-2">
                          {formatCurrency(selectedItem.subtotal)}
                        </p>
                      </div>
                      <div className="col-md-6">
                        <strong>Total:</strong>
                        <p className="mb-2">
                          {formatCurrency(selectedItem.total)}
                        </p>
                      </div>
                    </div>
                    {/* ---------- FIM DA LINHA PRINCIPAL ---------- */}

                    <hr />

                    <h6>Procedimentos</h6>
                    {selectedItem.items.map((sub, idx) => (
                      <div
                        key={idx}
                        className="bg-light p-3 rounded mb-2 d-flex flex-column">
                        <p className="mb-1">
                          <strong>Procedimento:</strong> {sub.procedure_name}
                        </p>
                        <p className="mb-1">
                          <strong>Executante:</strong>{" "}
                          {sub.executing_company || "N/A"}
                        </p>
                        <p className="mb-1">
                          <strong>Solicitante:</strong>{" "}
                          {sub.requesting_company || "N/A"}
                        </p>
                        <p className="mb-1">
                          <strong>Quantidade:</strong> {sub.quantity}
                        </p>
                        <p className="mb-1">
                          <strong>Preço (Venda):</strong>{" "}
                          {formatCurrency(sub.selling_price)}
                        </p>
                        <p className="mb-1">
                          <strong>Preço (Repasse):</strong>{" "}
                          {formatCurrency(sub.cost_price)}
                        </p>
                      </div>
                    ))}

                    <hr />

                    <h6>Pagamentos</h6>
                    {selectedItem.payments.map((payment, index) => (
                      <div key={index} className="bg-light p-3 rounded mb-2">
                        <div className="row g-2">
                          <div className="col-md-4">
                            <strong>Método:</strong> {payment.method}
                          </div>
                          <div className="col-md-4">
                            <strong>Valor:</strong>{" "}
                            {formatCurrency(payment.amount)}
                          </div>
                          <div className="col-md-4">
                            <strong>Líquido:</strong>{" "}
                            {formatCurrency(payment.net_amount)}
                          </div>

                          {/* --------- Exibir Taxa de Cartão --------- */}
                          <div className="col-md-4">
                            <strong>Taxa (%):</strong> {payment.fee_percent}%
                          </div>
                          <div className="col-md-4">
                            <strong>Valor da Taxa:</strong>{" "}
                            {formatCurrency(payment.fee_value)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setSelectedItem(null)}>
                      Fechar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <tfoot style={{ padding: "1rem", textAlign: "center" }}>
          <tr className="bg-body-tertiary align-middle py-2">
            {/* Cabeçalho do total */}
            <td colSpan={5} className="text-end pe-4">
              <span className="text-uppercase fw-bold text-secondary">
                Total por método
              </span>
            </td>

            <td className="pe-3">
              <div className="d-flex align-items-center gap-2 text-success fw-semibold">
                <img src={pix} width={32} alt="Pix" />
                {formatCurrency(paymentTotals.pix)}
                <span className="badge bg-success-subtle text-success rounded-pill">
                  PIX
                </span>
              </div>
            </td>

            {/* Crédito */}
            <td className="pe-3">
              <div className="d-flex align-items-center gap-2 text-success fw-semibold">
                <CreditCard size={16} />
                {formatCurrency(paymentTotals.credit)}
                <span className="badge bg-success-subtle text-success rounded-pill">
                  crédito
                </span>
              </div>
            </td>

            {/* Débito */}
            <td className="pe-3">
              <div className="d-flex align-items-center gap-2 text-info fw-semibold">
                <Landmark size={16} />
                {formatCurrency(paymentTotals.debit)}
                <span className="badge bg-info-subtle text-info rounded-pill">
                  débito
                </span>
              </div>
            </td>

            {/* Dinheiro */}
            <td className="pe-3">
              <div className="d-flex align-items-center gap-2 text-warning fw-semibold">
                <Banknote size={16} />
                {formatCurrency(paymentTotals.cash)}
                <span className="badge bg-warning-subtle text-warning rounded-pill">
                  dinheiro
                </span>
              </div>
            </td>

            {/* Cortesia */}
            <td>
              <div className="d-flex align-items-center gap-2 text-primary fw-semibold">
                <Gift size={16} />
                {formatCurrency(paymentTotals.courtesy)}
                <span className="badge bg-primary-subtle text-primary rounded-pill">
                  cortesia
                </span>
              </div>
            </td>
          </tr>
        </tfoot>
      </div>
    </>
  );

  return <Menu screen={screenTela} />;
};

export default Cashiers;
